import { nanoid } from "nanoid";
import { createFetchWithRetry } from "$lib/utils/fetchHelpers";
import { logger } from "$lib/utils/logger";
import {
  APP_NAME,
  PLAY_EVENT,
  POP_EVENT,
  type DataPipelinePlayEventPayload,
  type Environment,
  type DataPipelineEvent,
  type DataPipelinePopEventPayload,
  type DataPipelineEventPayload,
  type PlayEventContent,
} from "./eventTypes";
import {
  isAdContent,
  isNoticeContent,
  type Content,
} from "$lib/elev8Api/contentTypes";
import { EventQueue } from "./eventQueue";
import { reportError } from "$lib/errorHandling/errorReporter";
import { DateHelper } from "$lib/utils/dateHelper";
import {
  createHealthMonitor,
  type HealthMonitor,
} from "$lib/errorHandling/healthMonitor";

const DEFAULT_UPLOAD_INTERVAL = 60 * 1000;
const DEFAULT_UPLOAD_BATCH_LIMIT = 20;
const DEFAULT_UPLOAD_MAX_BYTES = 14_000;
const UPLOAD_UNHEALTHY_AFTER_MS = 10 * 60 * 1000;

let displayId = "";
let env: Environment = "production";
let uploadMaxBytes = DEFAULT_UPLOAD_MAX_BYTES;
let intervalTimer: NodeJS.Timeout | null = null;
let uploadHealth: HealthMonitor | null = null;
const eventQueue = new EventQueue();

const fetch = createFetchWithRetry({
  logContext: "DataPipeline",
  maxRetriesCount: 2,
  retryOnHttpStatusCodes: [],
});

function initDataPipeline(_displayId: string, _env: Environment) {
  env = _env;
  displayId = _displayId;
  uploadHealth = createHealthMonitor({
    name: "data_pipeline_upload",
    unhealthyAfterMs: UPLOAD_UNHEALTHY_AFTER_MS,
    onUnhealthy: (lastError, ctx) => {
      reportError("[Pipeline] Upload unhealthy", undefined, {
        source: "data_pipeline_upload",
        consecutive_failures: String(ctx.consecutiveFailures),
        failure_streak_ms: String(ctx.failureStreakMs),
        last_error_name: lastError.name,
        last_error_message: lastError.message,
      });
    },
  });
  intervalTimer = setInterval(() => {
    upload();
  }, DEFAULT_UPLOAD_INTERVAL);
  logger.info("[Pipeline] Data pipeline initialized");
}

function cleanupDataPipeline() {
  if (intervalTimer) {
    clearInterval(intervalTimer);
    intervalTimer = null;
  }
  upload();
}

function setUploadMaxBytes(bytes: number) {
  uploadMaxBytes = bytes;
  logger.debug("[Pipeline] Upload max bytes set to", uploadMaxBytes);
}

function recordPlayEvent(content: Content) {
  if (!contentGoesToPipeline(content)) {
    logger.warn(
      "[Pipeline] Content does not go to pipeline. Skipping play event.",
      content
    );
    return;
  }
  const payload: DataPipelinePlayEventPayload = {
    display_id: displayId,
    app: APP_NAME,
    event: PLAY_EVENT,
    content: content as PlayEventContent, // duration guaranteed by applyContentDefaults
    env,
  };
  const playEvent = createDataPipelineEvent(payload);
  logger.debug("[Pipeline] Recording play event", playEvent);
  if (!displayId) {
    logger.debug(
      "[Pipeline] Not initialized (no DataPipeline mounted). Skipping."
    );
    return;
  }
  eventQueue.enqueue(playEvent);
}

function recordPopEvent({
  provider,
  status,
  statusMessage,
  response,
  callback_url,
  creative_id,
}: Partial<DataPipelinePopEventPayload>) {
  const payload: DataPipelinePopEventPayload = {
    display_id: displayId,
    app: APP_NAME,
    event: POP_EVENT,
    provider: provider || "unknown",
    status: status || "UNKNOWN",
    statusMessage: statusMessage || "",
    response: response || "",
    callback_url: callback_url || "",
    creative_id: creative_id || "",
  };
  const popEvent = createDataPipelineEvent(payload);
  logger.debug("[Pipeline] Recording pop event", popEvent);
  if (!displayId) {
    reportError(
      "[Pipeline] Display ID is not set. Cannot record pop event.",
      undefined,
      { pop_event: JSON.stringify(popEvent) }
    );
    return;
  }
  eventQueue.enqueue(popEvent);
}

function createDataPipelineEvent(
  payload: DataPipelineEventPayload,
  metadata?: any
): DataPipelineEvent {
  return {
    uuid: nanoid(),
    start_at_utc: DateHelper.nowWithServerOffset(),
    start_at_local: formatLocalDateForBigQuery(
      DateHelper.newDateWithServerOffset()
    ),
    payload,
    metadata: metadata || {},
  };
}

async function upload() {
  const url = import.meta.env.VITE_DATAPIPELINE_URL;
  if (eventQueue.isEmpty()) {
    logger.debug("[Pipeline] No events to upload");
    return;
  }
  logger.debug(`[Pipeline] Starting upload, queue size: ${eventQueue.size()}`);
  while (!eventQueue.isEmpty()) {
    const batch = eventQueue.dequeueBatchByBytes(
      uploadMaxBytes,
      DEFAULT_UPLOAD_BATCH_LIMIT
    );
    const body = JSON.stringify(batch);
    const bodyBytes = body.length;

    if (bodyBytes > uploadMaxBytes) {
      reportError("[Pipeline] Oversize batch", undefined, {
        body_bytes: String(bodyBytes),
        batch_count: String(batch.length),
        upload_max_bytes: String(uploadMaxBytes),
      });
    }

    try {
      if (import.meta.env.MODE !== "production") {
        logger.info(
          `[Pipeline] FAKE (dev) 'uploading' ${batch.length} events (${bodyBytes} bytes)`,
          batch
        );
        continue;
      }
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
      });
      if (!response.ok) {
        throw new Error(`[Pipeline] HTTP error! Status: ${response.status}`);
      }
      logger.debug(
        `[Pipeline] Successfully uploaded ${batch.length} events (${bodyBytes} bytes)`,
        batch
      );
      uploadHealth?.recordSuccess();
    } catch (error) {
      logger.error("[Pipeline] Failed to upload events", error);
      uploadHealth?.recordFailure(error);
      eventQueue.requeueBatch(batch);
      break;
    }
  }
  logger.debug("[Pipeline] Upload complete");
}

function contentGoesToPipeline(content: Content) {
  return isAdContent(content) || isNoticeContent(content);
}

// Helper function to format a Date object to YYYY-MM-DD HH:MM:SS in local time
function formatLocalDateForBigQuery(date: Date): string {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, "0"); // getMonth() is 0-indexed
  const day = date.getDate().toString().padStart(2, "0");
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const seconds = date.getSeconds().toString().padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

// Helper function to safely extract response body, parsing JSON if possible (to avoid double escapes when stringified later)
async function getResponseBody(response: Response): Promise<unknown> {
  let responseBody: unknown = "";
  try {
    const text = await response.clone().text();
    try {
      responseBody = JSON.parse(text);
    } catch {
      responseBody = text;
    }
  } catch (e) {
    responseBody = `Body read error: ${(e as Error).message ?? "UNKNOWN ERROR"}`;
  }
  return responseBody;
}

export {
  initDataPipeline,
  cleanupDataPipeline,
  setUploadMaxBytes,
  recordPlayEvent,
  recordPopEvent,
  getResponseBody,
  DEFAULT_UPLOAD_INTERVAL,
  DEFAULT_UPLOAD_MAX_BYTES,
};
